import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Casino as SpinIcon,
  Close as RemoveIcon,
  EmojiEvents as TrophyIcon,
  PersonOff as ExcludeIcon,
  PersonAdd as IncludeIcon,
  Refresh as RefreshIcon,
  Stop as EndIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import io from 'socket.io-client';
import { useSnackbar } from 'notistack';
import LiveWheelDisplay from '../../Common/LiveWheelDisplay';
import { useSpinWheelHostSession } from './useSpinWheelHostSession';
import { SW_GAME_TYPES, SW_PHASES, isWheelVisiblePhase } from '../../UserSide/pages/spinHostPhases';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const getToken = () => localStorage.getItem('auth_token');

const getDisplayName = (entry) => {
  if (entry.display_name?.trim()) return entry.display_name.trim();
  if (entry.email) {
    const local = entry.email.split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1).replace(/[._]/g, ' ');
  }
  return `User ${entry.id || ''}`;
};

const getInitials = (name) =>
  (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const EntryCard = ({ entry, sessionId, onUpdate, busy }) => {
  const [colorOpen, setColorOpen] = useState(false);
  const authHeader = { Authorization: `Bearer ${getToken()}` };

  const toggle = async () => {
    await fetch(
      `${API_BASE}/api/spinning-wheel/admin/live/session/${sessionId}/entries/${entry.id}/toggle`,
      { method: 'POST', headers: authHeader }
    );
    onUpdate();
  };

  const remove = async () => {
    await fetch(
      `${API_BASE}/api/spinning-wheel/admin/live/session/${sessionId}/entries/${entry.id}`,
      { method: 'DELETE', headers: authHeader }
    );
    onUpdate();
  };

  const changeColor = async (color) => {
    await fetch(
      `${API_BASE}/api/spinning-wheel/admin/live/session/${sessionId}/entries/${entry.id}/color`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ color }),
      }
    );
    setColorOpen(false);
    onUpdate();
  };

  const isExcluded = !!entry.is_excluded;
  const isWinner = !!entry.is_winner;
  const name = getDisplayName(entry);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.8,
        borderRadius: 1.5,
        mb: 0.5,
        opacity: isExcluded ? 0.45 : 1,
        bgcolor: isWinner
          ? 'rgba(255,215,0,0.12)'
          : isExcluded
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(255,255,255,0.06)',
        border: isWinner ? '1px solid rgba(255,215,0,0.4)' : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <Tooltip title="Change slice color">
        <Box
          onClick={() => setColorOpen((p) => !p)}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: entry.slice_color || '#FF6B6B',
            cursor: 'pointer',
            border: '2px solid rgba(255,255,255,0.3)',
            flexShrink: 0,
          }}
        />
      </Tooltip>

      {colorOpen && (
        <input
          type="color"
          defaultValue={entry.slice_color || '#FF6B6B'}
          style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
          onChange={(e) => changeColor(e.target.value)}
          onBlur={() => setColorOpen(false)}
          autoFocus
        />
      )}

      <Avatar
        src={entry.avatar_url || undefined}
        sx={{ width: 32, height: 32, fontSize: 12, bgcolor: entry.slice_color || '#555' }}
      >
        {getInitials(name)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: isWinner ? 700 : 400, color: isWinner ? '#FFD700' : 'inherit' }}
        >
          {isWinner && <TrophyIcon sx={{ fontSize: 14, mr: 0.4, verticalAlign: 'middle' }} />}
          {name}
        </Typography>
        {entry.email && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {entry.email}
          </Typography>
        )}
        {!entry.email && entry.current_page && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {entry.current_page}
          </Typography>
        )}
      </Box>

      <Chip
        label={entry.user_type === 'registered' ? 'user' : entry.user_type || 'anon'}
        size="small"
        sx={{ fontSize: 10, height: 18 }}
        color={entry.user_type === 'registered' ? 'primary' : 'default'}
      />

      <Tooltip title={isExcluded ? 'Include in spin' : 'Exclude from spin'}>
        <IconButton size="small" onClick={toggle} disabled={busy}>
          {isExcluded ? <IncludeIcon fontSize="small" /> : <ExcludeIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove from wheel">
        <IconButton size="small" color="error" onClick={remove} disabled={busy}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const SpinWheelLiveHostTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    host,
    loading: hostLoading,
    busy: hostBusy,
    error: hostError,
    setError: setHostError,
    startSession,
    gameAboutToStart,
    force,
    buildWheel,
    startWheel,
    endSession,
    phase,
  } = useSpinWheelHostSession({ gameType: SW_GAME_TYPES.USER_WHEEL });

  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [titleInput, setTitleInput] = useState('Users Wheel');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewUsers, setPreviewUsers] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [spinning, setSpinning] = useState(false);
  const [finalAngle, setFinalAngle] = useState(0);
  const [spinDuration, setSpinDuration] = useState(6);
  const [winner, setWinner] = useState(null);
  const [animationComplete, setAnimationComplete] = useState(false);

  const authHeader = { Authorization: `Bearer ${getToken()}` };
  const combinedBusy = busy || hostBusy;
  const showConfig =
    host &&
    (phase === SW_PHASES.GAME_ABOUT_TO_START ||
      phase === SW_PHASES.FORCE ||
      phase === SW_PHASES.BUILDING_WHEEL ||
      isWheelVisiblePhase(phase));
  const showWheel = host && isWheelVisiblePhase(phase) && session;
  const showPostIntroActions =
    host &&
    [
      SW_PHASES.GAME_ABOUT_TO_START,
      SW_PHASES.FORCE,
      SW_PHASES.BUILDING_WHEEL,
      SW_PHASES.WHEEL_READY,
      SW_PHASES.SPINNING,
      SW_PHASES.RESULT,
    ].includes(phase);

  const loadSession = useCallback(async () => {
    if (!host?.linkedLiveSessionId) {
      setSession(null);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/live/session`, {
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success) {
        setSession(data.session);
        if (data.session?.winner_entry_id) {
          const w = (data.session.entries || []).find((e) => e.id === data.session.winner_entry_id);
          if (w) setWinner(w);
        }
      }
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host?.linkedLiveSessionId]);

  const loadPreviewUsers = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetch(
        `${API_BASE}/api/spinning-wheel/admin/live/active-users${params}`,
        { headers: authHeader }
      );
      const data = await res.json();
      setPreviewUsers(data.users || []);
    } catch (e) {
      enqueueSnackbar('Failed to load active users', { variant: 'error' });
    } finally {
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    loadPreviewUsers();
  }, [loadPreviewUsers]);

  useEffect(() => {
    loadSession();
  }, [loadSession, phase]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ['websocket'] });
    socket.on('connect', () => socket.emit('spinwheel:join-live'));
    socket.on('spinwheel:live_updated', (payload) => {
      if (payload?.hotSeat) return;
      const s = payload?.session;
      if (s != null && !spinning) setSession(s);
    });
    socket.on('spinwheel:spin_complete', ({ winner: w, session: s }) => {
      if (s) setSession(s);
      if (w) setWinner(w);
    });
    socket.on('spinwheel:ended', () => {
      setSession((prev) => (prev ? { ...prev, status: 'finished' } : prev));
    });
    return () => {
      socket.emit('spinwheel:leave-live');
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const post = async (path, body = {}) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/live${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action failed');
      return data;
    } catch (e) {
      setError(e.message);
      enqueueSnackbar(e.message, { variant: 'error' });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, successMsg) => {
    try {
      await fn();
      if (successMsg) enqueueSnackbar(successMsg, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message || 'Action failed', { variant: 'error' });
    }
  };

  const handleStartHost = () =>
    run(
      () => startSession({ title: titleInput || 'Users Wheel' }),
      'Users Wheel intro started'
    );

  const handleBuild = () =>
    run(async () => {
      await buildWheel({
        title: titleInput || host?.title || 'Users Wheel',
        status_filter: statusFilter,
      });
      await loadSession();
    }, 'Building the wheel on user screens');

  const handleRefresh = async () => {
    if (!session) return;
    const data = await post(`/session/${session.id}/refresh`);
    if (data?.session) {
      setSession(data.session);
      enqueueSnackbar(`${data.added} new participant(s) added`, { variant: 'success' });
    }
  };

  const handleSpin = async () => {
    if (!session || spinning) return;
    setWinner(null);
    setAnimationComplete(false);
    const data = await post(`/session/${session.id}/spin`);
    if (!data) return;
    setFinalAngle(data.finalAngle);
    setSpinDuration(data.spinDuration);
    setSpinning(true);
    if (data.winner) setWinner(data.winner);
  };

  const handleAnimationEnd = useCallback(() => {
    setSpinning(false);
    setAnimationComplete(true);
    loadSession();
  }, [loadSession]);

  const handleEnd = async () => {
    try {
      await endSession();
    } catch (e) {
      enqueueSnackbar(e.message || 'Failed to end', { variant: 'error' });
    }
    setWinner(null);
    setSpinning(false);
    setAnimationComplete(false);
    setSession(null);
  };

  const entries = session?.entries || [];
  const eligible = entries.filter((e) => !e.is_excluded);
  const excluded = entries.filter((e) => e.is_excluded);
  const winners = entries.filter((e) => e.is_winner);
  const displayError = error || hostError;

  if (hostLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
        Spin the Wheel — Users Wheel Panel
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You control the Play with Host → Users Wheel flow. Users advance only when you click each stage.
      </Typography>

      {displayError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setError(null);
            setHostError(null);
          }}
        >
          {displayError}
        </Alert>
      )}

      {!host ? (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Start Users Wheel (Play with Host)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Wheel title"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['all', 'active', 'idle', 'away'].map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    clickable
                    size="small"
                    variant={statusFilter === s ? 'filled' : 'outlined'}
                    color={statusFilter === s ? 'primary' : 'default'}
                    onClick={() => setStatusFilter(s)}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Status filter used when building the wheel
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                startIcon={<StartIcon />}
                disabled={combinedBusy}
                onClick={handleStartHost}
                fullWidth
              >
                Start Users Wheel
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {previewLoading
                ? 'Loading…'
                : `${previewUsers.length} active user(s) available for later build`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 100, overflow: 'auto' }}>
              {previewUsers.map((u, i) => (
                <Chip
                  key={u.device_id || u.session_id || i}
                  avatar={
                    <Avatar sx={{ width: 20, height: 20, fontSize: 9 }}>
                      {(u.display_name || '?')[0]}
                    </Avatar>
                  }
                  label={u.display_name}
                  size="small"
                  sx={{ fontSize: 11 }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#1E1E1E' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
              {host.title}
              <Chip label={phase} size="small" sx={{ ml: 1 }} color="success" />
              {session && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {eligible.length} eligible · spin #{session.spin_count || 0}
                </Typography>
              )}
            </Typography>
            {session && (phase === SW_PHASES.BUILDING_WHEEL || isWheelVisiblePhase(phase)) && (
              <Tooltip title="Add newly active users to the wheel">
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={combinedBusy || spinning}
                >
                  Sync users
                </Button>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<EndIcon />}
              disabled={combinedBusy || spinning}
              onClick={handleEnd}
            >
              End session
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {phase === SW_PHASES.INTRO && (
              <Button
                variant="contained"
                disabled={combinedBusy}
                onClick={() => run(gameAboutToStart, 'Game about to start')}
              >
                Game About To Start
              </Button>
            )}
            {showPostIntroActions && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={combinedBusy}
                  onClick={() => run(force, 'Force triggered (+ MYM force join)')}
                >
                  Force
                </Button>
                {!isWheelVisiblePhase(phase) && (
                  <Button variant="contained" disabled={combinedBusy} onClick={handleBuild}>
                    {phase === SW_PHASES.BUILDING_WHEEL ? 'Rebuild Config' : 'Start / Edit Config'}
                  </Button>
                )}
              </>
            )}
            {phase === SW_PHASES.BUILDING_WHEEL && (
              <Button
                variant="contained"
                color="success"
                disabled={combinedBusy}
                onClick={() =>
                  run(async () => {
                    await startWheel();
                    await loadSession();
                  }, 'Wheel is live for users')
                }
              >
                Start Wheel
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {showConfig && !showWheel && phase === SW_PHASES.BUILDING_WHEEL && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Users see &quot;Building The Wheel…&quot;. Edit participants below, then click{' '}
          <strong>Start Wheel</strong>.
        </Alert>
      )}

      {showConfig && session && (phase === SW_PHASES.BUILDING_WHEEL || showWheel) && (
        <>
          {winner && animationComplete && showWheel && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg,rgba(255,215,0,0.18),rgba(255,140,0,0.12))',
                border: '1px solid rgba(255,215,0,0.4)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <TrophyIcon sx={{ color: '#FFD700', fontSize: 30 }} />
                <Avatar
                  src={winner.avatar_url || undefined}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: winner.slice_color || '#FFD700',
                    border: '2px solid #FFD700',
                  }}
                >
                  {getInitials(getDisplayName(winner))}
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFD700', lineHeight: 1.2 }}>
                    {getDisplayName(winner)}
                  </Typography>
                </Box>
                <Chip label={`Spin #${session.spin_count}`} size="small" sx={{ ml: 1 }} />
              </Box>
            </Paper>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#1E1E1E', textAlign: 'center' }}>
                {showWheel ? (
                  <>
                    <LiveWheelDisplay
                      entries={entries}
                      spinning={spinning}
                      finalAngle={finalAngle}
                      spinDuration={spinDuration}
                      winnerEntry={winner}
                      animationComplete={animationComplete}
                      onAnimationEnd={handleAnimationEnd}
                      size={380}
                    />
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={
                          spinning ? <CircularProgress size={18} color="inherit" /> : <SpinIcon />
                        }
                        disabled={combinedBusy || spinning || eligible.length < 2}
                        onClick={handleSpin}
                        sx={{
                          background: spinning
                            ? undefined
                            : 'linear-gradient(135deg,#FF6B6B,#FFD700)',
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          px: 5,
                          py: 1.2,
                          borderRadius: 3,
                        }}
                      >
                        {spinning ? 'Spinning…' : 'SPIN!'}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 8 }}>
                    Wheel preview hidden until you click Start Wheel.
                    <br />
                    {eligible.length} eligible participant(s) ready.
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#1E1E1E',
                  maxHeight: 560,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Participants ({entries.length})
                </Typography>
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                  {winners.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFD700' }}>
                        WINNERS ({winners.length})
                      </Typography>
                      {winners.map((e) => (
                        <EntryCard
                          key={e.id}
                          entry={e}
                          sessionId={session.id}
                          onUpdate={loadSession}
                          busy={combinedBusy || spinning}
                        />
                      ))}
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}
                  {eligible.filter((e) => !e.is_winner).length > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        ELIGIBLE ({eligible.filter((e) => !e.is_winner).length})
                      </Typography>
                      {eligible
                        .filter((e) => !e.is_winner)
                        .map((e) => (
                          <EntryCard
                            key={e.id}
                            entry={e}
                            sessionId={session.id}
                            onUpdate={loadSession}
                            busy={combinedBusy || spinning}
                          />
                        ))}
                    </>
                  )}
                  {excluded.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        EXCLUDED ({excluded.length})
                      </Typography>
                      {excluded.map((e) => (
                        <EntryCard
                          key={e.id}
                          entry={e}
                          sessionId={session.id}
                          onUpdate={loadSession}
                          busy={combinedBusy || spinning}
                        />
                      ))}
                    </>
                  )}
                  {entries.length === 0 && <Alert severity="info">No participants yet.</Alert>}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default SpinWheelLiveHostTab;
